import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_BASE } from '../config';

export default function SharedDocumentPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [docInfo, setDocInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await fetch(`${API_BASE}/share/${token}`);
        const data = await response.json();
        
        if (!response.ok || !data.success) {
          setError(data.message || 'Ogiltig eller utgången länk');
          return;
        }
        
        setDocInfo(data.document);
      } catch (err) {
        setError('Kunde inte ladda dokumentet');
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [token]);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    
    try {
      console.log(`[Frontend] Starting download for token: ${token}`);
      const response = await fetch(`${API_BASE}/share/${token}/download`, {
        credentials: 'include'
      });
      
      console.log(`[Frontend] Response status: ${response.status}`);
      const data = await response.json();
      console.log(`[Frontend] Response data:`, data);
      
      if (!response.ok) {
        console.error(`[Frontend] Response not OK: ${response.status}`, data);
        alert(`Kunde inte ladda ner filen: ${data.message || 'Server error'}`);
        return;
      }
      
      if (!data.success) {
        console.error(`[Frontend] Response success=false:`, data);
        alert(`Kunde inte ladda ner filen: ${data.message || 'Unknown error'}`);
        return;
      }
      
      if (!data.file) {
        console.error(`[Frontend] No file data in response:`, data);
        alert('Kunde inte ladda ner filen: Ingen fil data mottagen');
        return;
      }
      
      console.log(`[Frontend] File received, size: ${data.file.length} chars base64`);
      
      // Convert base64 to blob
      const byteCharacters = atob(data.file);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: data.contentType || 'application/octet-stream' });
      
      // Create download link - use window.document to avoid conflict with state variable
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = downloadUrl;
      link.download = data.filename || docInfo?.namn || 'dokument';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      console.log(`[Frontend] Download initiated successfully`);
    } catch (err) {
      console.error(`[Frontend] Download error:`, err);
      alert(`Kunde inte ladda ner filen: ${err.message || 'Unknown error'}`);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <div>Laddar dokument...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
          <h2 style={{ color: 'var(--forest)', marginBottom: '0.5rem' }}>Ogiltig eller utgången länk</h2>
          <p style={{ color: 'var(--text-soft)', marginBottom: '1.5rem' }}>
            {error}
          </p>
          <Link to="/dokument" style={{
            display: 'inline-block',
            padding: '0.6rem 1.2rem',
            background: 'var(--forest)',
            color: '#fff',
            textDecoration: 'none',
            borderRadius: 4,
            fontWeight: 500
          }}>
            Till dokumentarkivet
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--off-white, #f7f6f2)' }}>
      {/* Header */}
      <div style={{ background: 'var(--forest)', color: '#fff', padding: '3rem 1.5rem 2.5rem' }}>
        <div className="container" style={{ maxWidth: 820, margin: '0 auto' }}>
          <Link to="/" style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
            fontSize: '0.78rem', color: 'rgba(255,255,255,.65)', textDecoration: 'none',
            letterSpacing: '0.04em', marginBottom: '1.25rem'
          }}>
            ← Startsidan
          </Link>
          <h1 style={{ fontFamily: '"Playfair Display", serif', fontSize: '2.2rem', margin: '0 0 0.5rem', fontWeight: 700 }}>
            Delat dokument
          </h1>
          <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,.75)', margin: 0 }}>
            Du har fått tillgång till ett dokument via delningslänk
          </p>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '2rem 1.5rem' }}>
        <div style={{
          background: 'var(--white)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: '1.5rem'
        }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem',
              background: 'var(--cream)',
              borderRadius: 4,
              marginBottom: '1rem'
            }}>
              <span style={{ fontSize: '1.5rem' }}>📄</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: '1rem', marginBottom: '0.25rem' }}>
                  {docInfo?.namn}
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-soft)' }}>
                  {docInfo?.kategori} · {docInfo?.datum}
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleDownload}
            disabled={downloading}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              width: '100%',
              padding: '0.85rem 1.5rem',
              background: 'var(--forest)',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: downloading ? 'wait' : 'pointer',
              opacity: downloading ? 0.7 : 1
            }}
          >
            {downloading ? '⏳ Laddar...' : '⬇ Ladda ner dokument'}
          </button>

          <p style={{
            fontSize: '0.75rem',
            color: 'var(--text-soft)',
            marginTop: '1rem',
            textAlign: 'center'
          }}>
            Denna delningslänk är giltig i 60 dagar från skapandedatumet.
          </p>
        </div>
      </div>
    </div>
  );
}
